export type NightscoutDataType = "entries" | "treatments" | "devicestatus";

export interface TransferNightscoutDataRequest {
  source_url: string;
  source_api_secret: string;
  dest_url: string;
  dest_api_secret: string;
  data_types?: string[];
  date_from?: string;
  date_to?: string;
}

export interface TransferNightscoutDataProgress {
  success: boolean;
  message?: string;
  error?: string;
  in_progress: boolean;
  completed: boolean;
  results?: {
    entries?: { count: number; total: number; error?: string };
    treatments?: { count: number; total: number; error?: string };
    profile?: { count: number; total: number; error?: string };
  };
  source_url: string;
  dest_url: string;
  date_from: string;
  date_to: string;
  start_datetime: string;
  end_datetime: string;
}

export const transferNightscoutData = async (
  data: TransferNightscoutDataRequest
): Promise<{ success: boolean; error: string | null }> => {
  try {
    const accessToken = localStorage.getItem("access_token");

    const response = await fetch(
      `${process.env.REACT_APP_BE_URL}/transfer-ns-data`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
        },
        body: JSON.stringify(data),
      }
    );

    const responseData = await response.json();
    if (!responseData.success) {
      console.error("Error starting Nightscout data transfer:", responseData);
      return {
        success: false,
        error:
          responseData?.error ||
          "Failed to start Nightscout data transfer. Please try again or contact support.",
      };
    }

    return { success: true, error: null };
  } catch (error) {
    console.error("Error starting Nightscout data transfer:", error);

    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : "Unknown error occurred. Please try again or contact support.",
    };
  }
};

export const stopTransferNightscoutData = async (): Promise<{
  success: boolean;
  error: string | null;
}> => {
  try {
    const accessToken = localStorage.getItem("access_token");
    const response = await fetch(
      `${process.env.REACT_APP_BE_URL}/stop-transfer-ns-data`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    const responseData = await response.json();
    if (!responseData.success) {
      console.error("Error stopping Nightscout data transfer:", responseData);
      return {
        success: false,
        error:
          responseData?.error ||
          "Failed to stop Nightscout data transfer. Please try again or contact support.",
      };
    }
    return { success: true, error: null };
  } catch (error) {
    console.error("Error stopping Nightscout data transfer:", error);
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : "Unknown error occurred. Please try again or contact support.",
    };
  }
};

export interface DeleteNightscoutDataRequest {
  url: string;
  api_secret: string;
  data_types: Array<"entries" | "treatments" | "devicestatus">;
  date_from?: string;
  date_to?: string;
}

export interface DeleteNightscoutDataResponse {
  success: boolean;
  message?: string;
  error?: string;
  results?: {
    entries?: { success: boolean; count: number; error?: string };
    treatments?: { success: boolean; count: number; error?: string };
    device?: { success: boolean; count: number; error?: string };
  };
}

export const getTransferNightscoutDataProgress =
  async (): Promise<TransferNightscoutDataProgress> => {
    const accessToken = localStorage.getItem("access_token");
    const response = await fetch(
      `${process.env.REACT_APP_BE_URL}/get-transfer-ns-status`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    return await response.json();
  };

export const deleteNightscoutData = async (
  data: DeleteNightscoutDataRequest
): Promise<DeleteNightscoutDataResponse> => {
  try {
    const accessToken = localStorage.getItem("access_token");
    const response = await fetch(
      `${process.env.REACT_APP_BE_URL}/delete-nightscout-data`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(data),
      }
    );

    if (response.ok) {
      const result: DeleteNightscoutDataResponse = await response.json();
      return result;
    } else {
      const errorData: DeleteNightscoutDataResponse = await response.json();
      return errorData;
    }
  } catch (error) {
    console.error("Error deleting Nightscout data:", error);
    return { success: false, error: error.toString() };
  }
};
